import set from 'lodash/set';

import { NavigationMapItem } from '@lumapps/navigation/types';
import createSlice, { PayloadAction } from '@lumapps/redux/createSlice';
import { moveInDirection } from '@lumapps/utils/array/moveInDirection';
import { Direction } from '@lumapps/utils/types/Direction';

import { nodeIsHidden } from '../../utils/nodes';

export type Modes = 'destination' | 'position';

export interface SetNodeDestinationDialogState {
    /** The whole tree to manipulate */
    nodes: Array<NavigationMapItem>;
    /** The current level displayed */
    displayedNodes: Array<NavigationMapItem>;
    /** The current mode set */
    mode: Modes;
    /** The node we want to select the destination for */
    nodeToMove: NavigationMapItem;
    /** The currently selected level */
    selectedNode: NavigationMapItem | null;
    /** The parent of the currently selected level */
    parentNode: NavigationMapItem | null;
    /** The current depth */
    depth: number;
    /** The position in which the node to move should be set */
    newPosition: number;
    /** Whether the edition dialog is opened */
    isEditOpen: boolean;
}

/**
 * Initial NavigationAdmin state
 */
const initialState: SetNodeDestinationDialogState = {
    nodes: [],
    displayedNodes: [],
    mode: 'destination',
    nodeToMove: {},
    selectedNode: null,
    parentNode: null,
    depth: 0,
    newPosition: 0,
    isEditOpen: false,
};

const initNodeDestinationNodeDialogState = (state: Pick<SetNodeDestinationDialogState, 'nodes' | 'nodeToMove'>) => ({
    ...initialState,
    ...state,
    displayedNodes: state.nodes,
});

const replaceDisplayedNodes = (
    state: SetNodeDestinationDialogState,
    newNodes: SetNodeDestinationDialogState['displayedNodes'],
) => {
    set(state, 'displayedNodes', newNodes);
    if (state.selectedNode) {
        set(state, 'selectedNode', { ...state.selectedNode, children: newNodes });
    }
};

export const reducers = {
    setDisplayedNodes(
        state: SetNodeDestinationDialogState,
        action: PayloadAction<SetNodeDestinationDialogState['displayedNodes']>,
    ) {
        replaceDisplayedNodes(state, action.payload);
    },
    selectNode(state: SetNodeDestinationDialogState, action: PayloadAction<NavigationMapItem>) {
        const { selectedNode, depth } = state;
        const node = action.payload;
        set(state, 'selectedNode', node);
        set(state, 'displayedNodes', node.children || []);
        set(state, 'parentNode', selectedNode);
        set(state, 'depth', depth + 1);
    },
    selectParentNode(state: SetNodeDestinationDialogState) {
        const { parentNode, selectedNode: currentSelectedNode, depth } = state;
        const selectedNode =
            !currentSelectedNode || (parentNode && parentNode.id === currentSelectedNode.id) ? null : parentNode;
        /**
         * If the selected node is exactly the same as the parent node, it means that we are
         * again at the top level, and therefore we need to set the selected node to null since
         * the next level will be the root level, and there is no selected node there.
         */
        set(state, 'selectedNode', selectedNode);
        set(state, 'displayedNodes', selectedNode ? selectedNode.children || [] : state.nodes);
        set(state, 'depth', depth - 1);
    },
    selectDestination(state: SetNodeDestinationDialogState) {
        const { displayedNodes, nodeToMove } = state;
        // In the case the user selects the same destination as the current, we first remove any node with the same id of the current level.
        const newList = displayedNodes.filter((node) => node.id !== nodeToMove.id);
        // We then push the new node at the end of the list
        newList.push(nodeToMove);
        // We store the current position to allow re-positioning
        set(state, 'newPosition', newList.length - 1);

        // Update the list with the new node
        reducers.setDisplayedNodes(state, { type: '', payload: newList });
        replaceDisplayedNodes(state, newList);
        // Set the mode to "position" to allow the user to see and position the node.
        set(state, 'mode', 'position');
    },
    updateNode(state: SetNodeDestinationDialogState, action: PayloadAction<NavigationMapItem>) {
        const { displayedNodes } = state;
        const newNode = action.payload;
        const newList = displayedNodes.map((node) => (node.id === newNode.id ? newNode : node));
        set(state, 'nodeToMove', newNode);
        replaceDisplayedNodes(state, newList);
    },
    moveNode(
        state: SetNodeDestinationDialogState,
        action: PayloadAction<{ node: NavigationMapItem; direction: Direction }>,
    ) {
        const { displayedNodes } = state;
        const { node, direction } = action.payload;

        const nodeIndex = displayedNodes.findIndex((displayedNode) => displayedNode.id === node.id);

        const { movedItems, newPosition } = moveInDirection(displayedNodes, nodeIndex, direction, (node) =>
            nodeIsHidden(node.partialPermission),
        );

        replaceDisplayedNodes(state, movedItems);
        set(state, 'newPosition', newPosition);
    },
    removeNode(state: SetNodeDestinationDialogState, action: PayloadAction<NavigationMapItem['id']>) {
        const { displayedNodes } = state;
        const newList = displayedNodes.filter((n) => n.id !== action.payload);
        replaceDisplayedNodes(state, newList);
        set(state, 'mode', 'destination');
    },
    toggleEdition(state: SetNodeDestinationDialogState) {
        set(state, ['isEditOpen'], !state.isEditOpen);
    },
};

const slice = createSlice({
    domain: 'set-node-destination',
    initialState,
    reducers,
});

const { actions, reducer } = slice;

export { actions, reducer, initialState, initNodeDestinationNodeDialogState };
